import React, { useState } from 'react';
import { HatGlasses, KeyRound, DatabaseZap, Loader2, FlaskConical, RefreshCw } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const ATTACK_TYPES = [
    {
        key: 'RansomwareAttack',
        label: 'Ransomware Attack',
        icon: HatGlasses,
        colorClass: 'critical', // red
    },
    {
        key: 'DataExfiltration',
        label: 'Data Exfiltration',
        icon: DatabaseZap,
        colorClass: 'high', // orange
    },
    {
        key: 'CredentialStuffing',
        label: 'Credential Stuffing',
        icon: KeyRound,
        colorClass: 'medium', // teal
    },
];


const SimulatorTab = ({ scanId }) => {
    const [selectedAttack, setSelectedAttack] = useState(null);
    const [loading, setLoading] = useState(false);
    const [simResult, setSimResult] = useState(null);
    const [error, setError] = useState(null);
    const [cached, setCached] = useState(false);
    const [tokensUsed, setTokensUsed] = useState(null);
    const [costUsd, setCostUsd] = useState(null);

    const handleAttackSelect = async (attack) => {
        setSelectedAttack(attack);
        setSimResult(null);
        setError(null);
        setTokensUsed(null);
        setCostUsd(null);
        setLoading(true);
        setCached(false);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        try {
            const cachedResponse = await fetch(`${ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Simulator")}?attackType=${attack.key}`, { method: 'POST', signal: controller.signal, credentials: 'include' });
            clearTimeout(timeoutId);
            if (cachedResponse.status === 200) {
                setCached(true);
                const json = await cachedResponse.json();
                const parsed = JSON.parse(json.response);
                setSimResult(parsed);
                setTokensUsed(json.tokens_used || null);
                setCostUsd(json.cost_usd || null);
            } else {
                await handleGenerate(attack);
            }
        } catch (err) {
            setError('Unable to retrieve simulation. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async (attack) => {
        try {
            setCached(false);
            setSimResult(null);
            setError(null);
            setTokensUsed(null);
            setCostUsd(null);
            setLoading(true);
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000);
            const response = await fetch(ApiNames.AI_HUB_SIMULATOR(scanId, attack.key), { method: 'POST', signal: controller.signal, credentials: 'include' });
            clearTimeout(timeoutId);
            if (!response.ok) throw new Error('Request failed');
            const json = await response.json();
            const parsed = JSON.parse(json.response);
            setSimResult(parsed);
            setTokensUsed(json.tokens_used || null);
            setCostUsd(json.cost_usd || null);
        } catch (err) {
            setError('Unable to generate simulation. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Helper to render simulation result inside the card
    const renderSimResult = (result, attack) => {
        if (!result) return null;
        return (
            <div className="mt-4">
                <h4 className="font-bold text-sm mb-2">{result.type}</h4>
                <p className="mb-3 text-black-400" style={{ whiteSpace: 'pre-line' }}>{result.summary}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    {result.detectionTime && (
                        <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col items-center justify-center min-w-0">
                            <span className="text-xl font-bold text-blue-400 text-center break-words w-full">
                                {result.detectionTime}
                            </span>
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight text-center mt-1">
                                Detection Time
                            </span>
                        </div>
                    )}

                    {result.investigationTime && (
                        <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col items-center justify-center min-w-0">
                            <span className="text-xl font-bold text-blue-400 text-center break-words w-full">
                                {result.investigationTime}
                            </span>
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight text-center mt-1">
                                Investigation Time
                            </span>
                        </div>
                    )}

                    {result.containmentEstimate && (
                        <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col items-center justify-center min-w-0">
                            <span className="text-xl font-bold text-blue-400 text-center break-words w-full">
                                {result.containmentEstimate}
                            </span>
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight text-center mt-1">
                                Containment Estimate
                            </span>
                        </div>
                    )}

                    {result.recoveryEstimate && (
                        <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col items-center justify-center min-w-0">
                            <span className="text-xl font-bold text-blue-400 text-center break-words w-full">
                                {result.recoveryEstimate}
                            </span>
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight text-center mt-1">
                                Recovery Estimate
                            </span>
                        </div>
                    )}

                    {result.complianceHit && (
                        <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col items-center justify-center min-w-0">
                            <span className="text-xl font-bold text-blue-400 text-center break-words w-full">
                                {result.complianceHit}%
                            </span>
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-tight text-center mt-1">
                                Compliance Hit
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex flex-row gap-2 mt-2 mb-2">
                    <button
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg shadow transition-all duration-150 flex items-center gap-2 text-xs"
                        onClick={() => handleGenerate(attack)}
                        disabled={loading || !scanId}
                    >
                        <RefreshCw className="w-4 h-4" /> Simulate Again
                    </button>
                    {loading && (
                        <span className="flex items-center text-xs text-blue-400 ml-2"><Loader2 className="animate-spin w-4 h-4 mr-1" />Simulating...</span>
                    )}
                </div>
                <div className="mt-1 text-[10px] text-gray-400 flex flex-wrap gap-2">
                    {tokensUsed && <span>Tokens used: {tokensUsed}. Cost: ${costUsd}</span>}
                    {cached && <span>Viewing cached simulation result.</span>}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col items-center gap-8">
            <div className="w-full max-w-6xl">
                <div className="card-header flex items-center justify-between mb-2">
                    <div className="card-title flex items-center gap-2">
                        <FlaskConical className="w-6 h-6 text-blue-400" />
                        <h3 className="font-bold text-lg">AI Incident Readiness Simulator</h3>
                    </div>
                    <span className="card-badge flex items-center gap-1 bg-amber-100 text-amber-600 px-3 py-1 rounded-full text-xs font-semibold">
                        <FlaskConical className="w-4 h-4 mr-1" />Simulation Mode
                    </span>
                </div>
                <p className="text-gray-400 mb-6 text-sm mt-2">
                    Select an attack scenario to simulate how your compliance posture would fare against real-world threats. AI simulates breach scenarios against your current compliance posture, measuring response readiness and predicting compliance impact.
                </p>
                <div className="scenario-cards grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-8">
                    {ATTACK_TYPES.map((attack) => {
                        const Icon = attack.icon;
                        // Card color classes
                        let cardColor = '';
                        if (attack.colorClass === 'critical') cardColor = 'critical';
                        else if (attack.colorClass === 'high') cardColor = 'high';
                        else if (attack.colorClass === 'medium') cardColor = 'medium';
                        const isActive = selectedAttack?.key === attack.key;
                        return (
                            <div
                                key={attack.key}
                                className={`scenario-card ${cardColor} flex flex-col items-center p-6 rounded-xl shadow-lg cursor-pointer transition-all duration-200 relative ${isActive ? 'active border-2 border-blue-500 scale-105 z-10' : 'border border-base-300 hover:border-blue-400'}`}
                                style={{ minHeight: 220 }}
                                onClick={() => !loading && handleAttackSelect(attack)}
                            >
                                <div className="top-bar w-full h-2 rounded-t-xl mb-4" />
                                <span className="scenario-icon flex items-center justify-center rounded-full mb-3" style={{ width: 48, height: 48 }}>
                                    <Icon className="w-8 h-8" />
                                </span>
                                <div className="scenario-header text-center mb-1">
                                    <h4 className="scenario-title font-semibold text-base mb-0">{attack.label}</h4>
                                </div>
                                {/* Only show result if selected */}
                                {isActive && (
                                    <div className="w-full">
                                        {error && (
                                            <div className="text-center text-red-400 py-4 text-xs">{error}</div>
                                        )}
                                        {(!simResult && loading) && (
                                            <div className="flex flex-col items-center justify-center py-6 animate-fadeIn">
                                                <Loader2 className="animate-spin text-blue-400 w-8 h-8 mb-2" />
                                                <span className="text-gray-400 text-xs">Simulating...</span>
                                            </div>
                                        )}
                                        {simResult && renderSimResult(simResult, attack)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default SimulatorTab;
